import { useId, useState, type ReactNode } from "react";
import { ChevronDown, ListFilter, Trash2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { Toolbar } from "./Toolbar";

/** One filter control in the panel. `value` is the human-readable chip text; null = not applied. */
export interface FilterField {
  key: string;
  /** Shown above the control when the panel is open, and as the chip prefix when it is closed. */
  label: string;
  value: string | null;
  onClear: () => void;
  /** Receives the id to put on the control so the label is associated with it. */
  render: (id: string) => ReactNode;
  /** Grid columns the control spans (a date range needs two). */
  span?: 1 | 2;
}

interface Props {
  search?: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    autoFocus?: boolean;
    wide?: boolean;
  };
  fields: FilterField[];
  onClearAll: () => void;
  right?: ReactNode;
  /** Open the panel on first render. Default: closed — applied filters show as chips instead. */
  defaultOpen?: boolean;
}

/**
 * List-page filter bar in the SalePro admin's layout: a "Lọc" toggle beside the search box (tinted
 * while filters are applied, with a "Xoá tất cả lọc (n)" button next to it), a labelled grid of
 * controls when open, and — when closed — a row of "Đang lọc theo:" chips, each removable on its
 * own. Keeps the filters out of the way until they are wanted, but never hides that they are on.
 */
export function FilterBar({ search, fields, onClearAll, right, defaultOpen = false }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const idBase = useId();
  const applied = fields.filter((f) => f.value != null && f.value !== "");

  return (
    <div className="mb-4 space-y-2">
      <Toolbar
        className="mb-0"
        search={search}
        filters={
          <>
            <Button
              type="button"
              size="sm"
              variant={applied.length ? "default" : "outline"}
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              aria-controls={`${idBase}-panel`}
            >
              <ListFilter aria-hidden="true" />
              Lọc
              {applied.length > 0 && <span className="tabular-nums">({applied.length})</span>}
              <ChevronDown
                aria-hidden="true"
                className={cn("transition-transform", open && "rotate-180")}
              />
            </Button>
            {applied.length > 0 && (
              <Button type="button" size="sm" variant="destructive" onClick={onClearAll}>
                <Trash2 aria-hidden="true" />
                Xoá tất cả lọc ({applied.length})
              </Button>
            )}
          </>
        }
        right={right}
      />

      {open ? (
        <div
          id={`${idBase}-panel`}
          role="group"
          aria-label="Bộ lọc"
          className="grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
        >
          {fields.map((f) => {
            const id = `${idBase}-${f.key}`;
            return (
              <div
                key={f.key}
                className={cn("min-w-0 space-y-1", f.span === 2 && "sm:col-span-2")}
              >
                <Label htmlFor={id} className="text-xs text-muted-foreground">
                  {f.label}
                </Label>
                {f.render(id)}
              </div>
            );
          })}
        </div>
      ) : (
        applied.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="filter-chips">
            <span className="mr-1 text-sm font-medium text-foreground">Đang lọc theo:</span>
            {applied.map((f) => (
              <span
                key={f.key}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary px-2 py-0.5 text-xs text-secondary-foreground"
              >
                <span className="text-muted-foreground">{f.label}:</span>
                <span className="font-medium">{f.value}</span>
                <button
                  type="button"
                  onClick={f.onClear}
                  aria-label={`Bỏ lọc ${f.label}`}
                  className="ml-0.5 rounded-sm text-muted-foreground hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="size-3" aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        )
      )}
    </div>
  );
}
